import React from 'react';
import {
  Component,
  FilterCombo,
  FORM_CONTROL_CONFIG,
  PuzzleButton,
  PuzzleInput,
  Container,
  VBox,
  HBox
} from "puzzle";
import { CUSTOM_ATTRIBUTES, CUSTOM_FIELD, FIELD_DATA } from "../../Types";
import { Field } from "../../../UI/Field/Field";
import _ from "lodash";
import { translateCustomAttributes } from "../../../util/Util";

export type PROPS = {};

export type STATE = {
  fields: FIELD_DATA[],
  fieldsData: any //object is generated dynamically
}

const initialState: STATE = {
  fields: [],
  fieldsData: {}
};

class CustomFields extends Component<PROPS, STATE> {
  static defaultProps = {};
  public state: STATE = initialState;
  tooltip: any;
  /**
   * Prefix for custom attributes name fields.
   * Used to scope custom attributes validation just to custom attributes fields
   * Ticket reference PDII-37174
   */
  namePrefix: string = 'c@_';

  customFieldsWrapperRef: any;

  /**
   * In case of TEXT custom attribute add customAttributeNameTooLong validation rule
   *
   * @param value
   */
  addTextFiledValidationRules = () => [(value: string) => ({
    valid: value.length <= 255,
    'data-error': 'v_customAttributeNameTooLong'
  })];

  /**
   * Generate custom fields for form control
   */
  customFieldsReducer = (acc: FORM_CONTROL_CONFIG, curr: FIELD_DATA) => {
    const { isTemplate } = this.props;
    return ({
      ...acc,
      [curr.name]: {
        required: isTemplate ? false : curr.mandatory,
        rules: curr.type === 'TEXT' ? this.addTextFiledValidationRules() : []
      }
    })
  };

  /**
   * Get data from fields
   */
  getData = (): CUSTOM_FIELD[] => {
    const { fieldsData } = this.state;
    const formData = this.props.form.getData();
    const parsedData: CUSTOM_FIELD[] = Object.keys(fieldsData).map((name: string) => ({
      name: name.replace(this.namePrefix, ''),
      value: formData[name] ? formData[name].trim() : null
    }));
    return parsedData.filter((item) => item.value);
  };

  /**
   * Handle FilterCombo change, sets fieldsData
   */
  handleFilterComboChange = (event: any) => {
    const { value, name = '' } = event.target.component;
    const e = {
      target: {
        name,
        value
      },
      value
    };
    this.props.form.handleChange(e);
  };

  /**
   * Reset to initial values
   */
  reset = () => this.setState(initialState);

  /**
   * Set data
   */
  setData = (data: CUSTOM_ATTRIBUTES[]) => {
    const { namePrefix } = this;
    data = data.map((item: CUSTOM_ATTRIBUTES) => ({
      ...item,
      name: `${item.name.substring(0, namePrefix.length) === namePrefix ? '' : namePrefix}${item.name}`
    }));
    let fieldsData: any = {};
    let { fields } = this.state;
    let i = 0;
    let fieldsLength = fields.length;
    let current;
    while (i < fieldsLength) {
      current = fields[i];
      let currentData = _.find(data, { name: current.name });
      let isCombo = current.type === 'COMBO';
      if (isCombo && currentData) {
        let isValueInData = current.values.includes(currentData.value);
        if (!isValueInData) {
          currentData.value = '';
        }
      }
      fieldsData[current.name] = !currentData ? '' : currentData.value;
      i++;
    }

    this.props.form.setData(fieldsData);
  };

  /**
   * Set fields
   */
  setFields = (customFields: FIELD_DATA[]) => {
    customFields = customFields.map((item: any) => ({
      ...item,
      name: `${this.props?.moduleId !== "CustomFieldsPopup" ? this.namePrefix : ''}${item.name}`
    }));
    const fieldsData = customFields.reduce((acc: any, curr) => ({ ...acc, [curr.name]: '' }), {});
    this.setState({ fields: customFields, fieldsData }, () => {
      const stateFields: FORM_CONTROL_CONFIG = customFields.reduce(this.customFieldsReducer, {});
      this.props.form.setFields(stateFields);
      this.props.setSectionVisible && this.props.setSectionVisible(!!customFields.length);
    });
  };

  /**
   * Creates custom field item.
   *
   * @param isTemplate
   * @param handleChange
   * @param fieldsData
   * @param item
   * @param index
   */
  createCustomField = (isTemplate: boolean, handleChange: any, fieldsData: any, item: FIELD_DATA, index: number) => {
    let fieldComponent;
    let itemLabel = item.name?.substring(this.namePrefix.length);
    switch (item.type) {
      case "COMBO":
        let fieldData = item.values ? item.values : [];
        let comboValue = fieldsData[item.name]?.value;
        comboValue = comboValue || null; // have to check if combo value is falsy since on puzzle X (button for removing value) is rendered even if combo value is an empty string
        fieldComponent =
          <Field
            mandatory={isTemplate ? false : item.mandatory}
            key={'field' + index}
            title={item.description}
          >
            <FilterCombo
              label={translateCustomAttributes(itemLabel)}
              name={item.name}
              data={fieldData}
              onChange={this.handleFilterComboChange}
              translateMethod={this.translate.bind(this)}
              errorTxt={fieldsData[item.name] && this.translateErrorMessage(fieldsData[item.name])}
              withValidation
              {...fieldsData[item.name]}
              value={comboValue}
              valueAsSimpleType={true}
            />
          </Field>;
        break;
      case "TEXT":
        fieldComponent =
          <Field
            key={index}
            title={item.description}
          >
            <PuzzleInput
              title={item.description}
              label={translateCustomAttributes(itemLabel)}
              id={`${item.name}CustomField`}
              fieldMandatory={isTemplate ? false : item.mandatory}
              name={item.name}
              onChange={handleChange}
              errorTxt={fieldsData[item.name] && this.translateErrorMessage(fieldsData[item.name])}
              withValidation
              {...fieldsData[item.name]}
            />
          </Field>
        break;
      default:
        console.error('Unsupported custom field type')
    }
    return fieldComponent;
  };

  renderBody(): false | JSX.Element {
    const { fields } = this.state;
    const { getFields, handleChange } = this.props.form;
    const { isTemplate } = this.props;
    const fieldsData = getFields();
    const haveFields: boolean = !!fields.length;
    return (
      <>
        {haveFields &&
          <Container
            className='tp-section-container tp-submission-page-custom-fields tp-100'
            aria-label={this.translate('SRL-section')}
            ref={wrapper => this.customFieldsWrapperRef = wrapper}
            htmlTag={'section'}
            tabIndex={0}
          >
            <Container className={'tp-section-shadow'}>
              <VBox gap={2} className={'tp-section-container-content'}>
                <HBox>
                  <Container
                    htmlTag={'h3'}
                    hBox
                    alignItemsCenter
                    className="tp-section-container-title tp-100 tp-font-size-md"
                  >
                    {this.translate('customFields')}
                  </Container>
                  {haveFields &&
                    <PuzzleButton
                      titleText={this.translate('SRL-openCustomFieldsInModal')}
                      onClick={() => {
                        this.dispatch('openSubmissionPopup', '', {
                          customFieldsState: this.state,
                          customFieldsProps: this.props,
                          popupName: 'customFields'
                        })
                      }}
                      aria-label={this.translate('SRL-openCustomFieldsInModal')}
                      iconClass={'tp-icon tp-open_in_new'}
                      fillMode={'flat'}
                      themeColor={'primary'}
                      tooltipPosition={'right'}
                      withTooltip
                    />
                  }
                </HBox>
                <HBox wrap className={`pd-custom-fields-wrap ${fields.length < 1 ? 'k-display-none' : ''}`}>
                  {fields.map(this.createCustomField.bind(this, isTemplate, handleChange, fieldsData))}
                </HBox>
              </VBox>
            </Container>
          </Container>
        }
      </>
    );
  }
}

export default CustomFields;
